import { UserDto } from '@act/shared/data-transfer-objects';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Button, ContentPreview } from '@act/storybook';
import Typography from '@mui/material/Typography';
import { HtmlDisplayerWithHyperlinks } from 'react-components/components/HtmlDisplayerWithHyperlinks';
import useInteractionsDialog from 'react-components/dialog-forms/hooks/use-interactions-dialog';
import useMemberStatusChangeRequestDialog from 'react-components/dialog-forms/hooks/use-member-status-change-request-dialog';
import { NeedMiniInfoBox } from 'react-components/needs/components/need-mini-info';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { metaSliceSelectors } from 'redux/slices/metaSlice';
import { needsSelectors } from 'redux/slices/needs.slice';
import { userAppSelectors } from 'redux/slices/usersSlice';
import { visitsAppSelectors } from 'redux/slices/visitsSlice';
import { useAppSelector } from 'redux/store-hooks';
import InfoCard, {
  AdditionalFeedCardActions,
} from 'toolkit/info-card/info-card';
import { TaggableChip } from '@act/storybook';
import { AddressFormatter } from 'utilities/formatters/address-formatter';
import { canMakeRequest } from './feed-card-utility';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { selectSelectedMember } from 'redux/slices/memberSlice';
import { Permission } from '@act/shared/authorization';

const FeedVisitCard = (props: {
  visitId: number;
  shouldHighlight?: boolean;
}) => {
  const openMemberStatusChangeRequestDialog =
    useMemberStatusChangeRequestDialog();

  const { editVisit } = useInteractionsDialog();

  const actingUser: UserDto = userAppSelectors.useSelectActingUser();

  const visit = visitsAppSelectors.useSelectVisitById(props.visitId);
  const member = membersAppSelectors.useSelectMemberById(visit?.subjectUserId);
  const author = userAppSelectors.useSelectUserById(visit?.createdBy);
  const isResourcing =
    !!visit?.resourceInteraction || !!visit?.needResourceInteraction;
  const resource =
    visit?.resourceInteraction?.resource ||
    visit?.needResourceInteraction?.resource ||
    null;
  const needId = visit?.needResourceInteraction?.userNeedId;
  const need = needsSelectors.useSelectNeedById(needId);

  // TODO: I don't like using this
  // because it assumes you are on the correct user's member page!!
  const selectedMember = useAppSelector(selectSelectedMember);

  const outcome = metaSliceSelectors.useSelectVisitOutcomeById(
    visit?.outcomeId
  );

  const isAdmin = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ACCESS_ADMIN_TOOLS
  );

  // If visit doesn't exist (e.g., after deletion), don't render the component
  if (!visit) {
    return null;
  }

  let outcomeString = 'Unknown';
  if (outcome) {
    outcomeString =
      outcome.key === 'other'
        ? visit.outcomeDetails || outcome.text
        : outcome.text;
  }

  const statusChips = [];
  const menuItems: AdditionalFeedCardActions[] = [];

  if (!visit.outcomeId) {
    statusChips.push(<TaggableChip isTag color="warning" label="Incomplete" />);
  } else if (visit.insufficientNotes) {
    statusChips.push(<TaggableChip isTag color="warning" label="Incomplete" />);
  }

  let location = 'Unknown Address';

  if (visit.addressId) {
    if (member) {
      const visitAddress = member.addresses.find(
        a => a.id === Number(visit.addressId)
      );
      if (visitAddress) {
        location = AddressFormatter.toShort(visitAddress);
      }
    }
  } else if (visit.location) {
    location = visit.location;
  }

  const expandedContent =
    visit.note || need ? (
      <ContentPreview>
        {!!visit.note && HtmlDisplayerWithHyperlinks({ note: visit.note })}
        {need && (
          <>
            <Typography variant="bodyLarge" component="p">
              Need:
            </Typography>
            <NeedMiniInfoBox need={need} />
          </>
        )}
      </ContentPreview>
    ) : null;

  const shouldShowStatusChangeRequest =
    isAdmin || canMakeRequest(visit.endTime, selectedMember);

  if (isAdmin || shouldShowStatusChangeRequest) {
    menuItems.push({
      title: 'Request status change',
      icon: shouldShowStatusChangeRequest ? null : (
        <AdminPanelSettingsIcon fontSize="small" />
      ),
      details: 'Request status change',
      handler: () => {
        void openMemberStatusChangeRequestDialog({
          memberId: member.id,
          interactionId: visit.interactionId,
          effectiveAt: visit.endTime,
        });
      },
    });
  }

  let authorString = 'Unknown';
  if (author) {
    if (author.id === actingUser.id) {
      authorString = 'You';
    } else {
      authorString = author.name;
    }
  }
  let visitExplanation = `${authorString} visited ${member?.name}`;
  if (isResourcing) {
    visitExplanation = `${authorString} visited ${resource.title} for ${member?.name}`;
  }

  return (
    <InfoCard
      shouldHighlight={props.shouldHighlight}
      subject={isResourcing ? 'Resourcing visit ' : 'Visit'}
      variant={isResourcing ? 'resource' : 'visit'}
      menuItems={menuItems}
      startTime={visit.startTime}
      endTime={visit.endTime}
      statusChips={statusChips}
      primaryOption={
        <Button
          variant="text"
          onClick={event => {
            event.stopPropagation();
            editVisit({
              visit: visit,
              userId: member?.id,
              need: need,
            });
          }}
        >
          Edit
        </Button>
      }
    >
      <>
        <Typography variant="labelLarge" component="p">
          {visitExplanation}
        </Typography>
        {!isResourcing && location}
        <Typography variant="bodyMedium" component="p">
          {outcomeString}
        </Typography>
        {expandedContent}
      </>
    </InfoCard>
  );
};

export default FeedVisitCard;
